import { patchState, signalStore, withMethods, withState } from '@ngrx/signals';
import { EditTrackPayload, SaveBulkResponse, TrackApiData, TrackData } from '@shared/interfacres/track';

import { inject } from '@angular/core';
import { TracksManagerService } from '@data-access/manager/services/tracks-manager.service';

import { firstValueFrom } from 'rxjs';

import type { GetFilter, GetPagination } from '@shared/interfacres/request-related';
import { MatSnackBar } from '@angular/material/snack-bar';
import { DEFAULT_PAGE_SIZE } from '@shared/constants/basic';


interface TracksSearchState {
  tracks: TrackData[];

  tracksTotal: number;
  isLoading: boolean;
  trackName: string;
  filter: GetFilter;
  pagination: GetPagination;
}

const initialState: TracksSearchState = {
  tracks: [],
  tracksTotal: 0,
  isLoading: false,
  trackName: '',
  filter: { query: 'releaseDate', order: 'desc' },
  pagination: { page: 1, pageSize: DEFAULT_PAGE_SIZE }
};

export const TracksManagerStore = signalStore(
  withState(initialState),

  withMethods((store, service = inject(TracksManagerService), snackBar = inject(MatSnackBar)) => ({

    getTrackByName(trackName: string) {
      patchState(store, () => ({
        trackName
      }));
      return this.getTracks();
    },

    setPagination(page: number, pageSize: number) {
      patchState(store, (state) => ({
        pagination: {
          ...state.pagination,
          page,
          pageSize,
        }
      }));
      return this.getTracks();
    },

    setFilter(query: string, order: 'asc' | 'desc') {
      patchState(store, (state) => ({
        filter: {
          ...state.filter,
          query,
          order,
        }
      }));
      return this.getTracks();
    },

    getSignedTrackUrlById(id: number) {
      return service.getSignedTrackUrlById(id);
    },
    clearCriteria() {
      patchState(store, () => ({
        trackName: '',
        filter: { query: 'releaseDate', order: 'desc' } as GetFilter,
        pagination: { page: 1, pageSize: DEFAULT_PAGE_SIZE }
      }));
    },

    async getTracksOnPage(page: number, pageSize: number) {
      if (store.trackName() !== '' || store.filter().order !== 'desc' || store.filter().query !== 'releaseDate') {
        return;
      }

      return firstValueFrom(service.getTracks('', store.filter(), {pageSize, page}));

    },


    async getTracks() {
      patchState(store, () => ({
        isLoading: true,
      }));

      const withPagination = await firstValueFrom(service.getTracks(store.trackName(), store.filter(), store.pagination()));
      patchState(store, () => ({
        isLoading: false,
        tracks: withPagination.data.map((track: TrackApiData) => {
          return {
            ...track,
            downloadsCount: track.downloads ? track.downloads[0]?.count : 0,
            artists: track.artists
          }
        }),
        tracksTotal: withPagination.pagination.total
      }));
    },
    async updatePartial(id: number, payload: Partial<EditTrackPayload>) {
      patchState(store, () => ({
        isLoading: true,
      }));
      try {
        await firstValueFrom(service.updatePartial(id, payload));
        await this.getTracks();
      } catch (e) {
        snackBar.open('Error during update', '', { duration: 2000, horizontalPosition: 'end', panelClass: 'error-toast' });
        console.error(e);
      } finally {
        patchState(store, () => ({
          isLoading: false
        }));
      }
    },

    async saveTracksData(sessionId: string, tracks: Partial<EditTrackPayload>[]) {
      patchState(store, () => ({
        isLoading: true,
      }));
      try {
        const response: SaveBulkResponse = await firstValueFrom(service.saveBulk(sessionId, tracks));
        if (response.successful === response.totalFiles) {
          snackBar.open('Success', '', { duration: 2000, horizontalPosition: 'end'});
        } else {
          snackBar.open('Error during save', `Successfully saved: ${response.successful}, errors: ${response.totalFiles-response.successful}`,
            { duration: 5000, horizontalPosition: 'end', panelClass: 'error-toast' });
        }
        this.clearCriteria();
        await this.getTracks();
      } catch (e) {
        snackBar.open('Error during update', '', { duration: 2000, horizontalPosition: 'end', panelClass: 'error-toast' });
        console.error(e);
      } finally {
        patchState(store, () => ({
          isLoading: false
        }));
      }
    },

    async delete(ids: number[]) {
      patchState(store, () => ({
        isLoading: true,
      }));

      try {
        await firstValueFrom(service.delete(ids));
        return this.getTracks();

      } catch (e) {
        snackBar.open('Error during removing', '', { duration: 2000, horizontalPosition: 'end', panelClass: 'error-toast' });
        console.error(e);
      } finally {
        patchState(store, () => ({
          isLoading: false
        }));
      }
    },


    async setPosition(id: number, prevPosition: number | null, nextPosition: number | null) {
      if (store.trackName() !== '' || store.filter().order !== 'desc' || store.filter().query !== 'releaseDate') {
        snackBar.open('Error', 'Clear all search and filters before reorder', { duration: 6000, horizontalPosition: 'end', panelClass: 'error-toast' });
        return;
      }
      patchState(store, () => ({
        isLoading: true,
      }));

      try {
        await firstValueFrom(service.setPosition(id, prevPosition, nextPosition));
        snackBar.open('Success', 'Reordering complete', { duration: 2000, horizontalPosition: 'end', panelClass: 'success-toast' });
        return this.getTracks();

      } catch (e) {
        snackBar.open('Error during reorder', '', { duration: 2000, horizontalPosition: 'end', panelClass: 'error-toast' });
        console.error(e);
      } finally {
        patchState(store, () => ({
          isLoading: false
        }));
      }
    }

  }))
);
